// @flow
import React from "react";
import classNames from "classnames";
import withStyles from "elevate-ui/withStyles";
import Typography from "elevate-ui/Typography";
import Phone from "elevate-ui-icons/Phone";
import Email from "elevate-ui-icons/Email";
import Link from "elevate-ui-icons/Link";
import { withGlobalContext } from "../common/GlobalContext";
import AgentBio from "./AgentBio";
import AgentSocial from "./AgentSocial";

import type { $Agent, ComplianceItems } from "flow/types";

type Props = {
  agent: $Agent,
  agentBio: Object,
  classes: Object,
  className: string,
  compliance: ComplianceItems,
};

const AgentProfile = ({
  agent,
  agentBio,
  classes,
  className,
  compliance,
}: Props) => {
  const rootClass = classNames(classes.root, className);
  const {
    cellPhoneFormatted,
    email,
    elevateSiteUrl,
    mlsWebsiteUrl,
    otherWebsiteUrl,
  } = agent;
  return (
    <div className={rootClass}>
      <div className={classes.profileTop}>
        {agent.picture && (
          <img className={classes.photo} src={agent.picture} alt={agent.name} />
        )}
        <Typography type="heading3">{agent.name}</Typography>
        {agent.jobTitle && (
          <div className={classes.jobTitle}>{agent.jobTitle}</div>
        )}
      </div>

      <div className={classes.profileBottom}>
        {agent.licenseNumber &&
          compliance &&
          compliance.agentOptions &&
          compliance.agentOptions.dreNumberOnAgentProfile && (
            <Typography className={classes.agentDetail} type="subHeading">
              <span
                className={classNames(
                  classes.agentDetailText,
                  "ref__agent-profile-drenumber"
                )}
              >
                DRE: {agent.licenseNumber}
              </span>
            </Typography>
          )}
        {cellPhoneFormatted && (
          <Typography className={classes.agentDetail} type="subHeading">
            <Phone size={18} className={classes.agentDetailIcon} />
            <span className={classes.agentDetailText}>
              <a href={`tel:${cellPhoneFormatted}`}>{cellPhoneFormatted}</a>
            </span>
          </Typography>
        )}
        {email && (
          <Typography type="subHeading" className={classes.agentDetail}>
            <Email size={18} className={classes.agentDetailIcon} />
            <span className={classes.agentDetailText}>
              <a href={`mailto:${email}`}>{email}</a>
            </span>
          </Typography>
        )}
        {elevateSiteUrl ? (
          <Typography type="subHeading" className={classes.agentDetail}>
            <Link size={18} className={classes.agentDetailIcon} />
            <span className={classes.agentDetailText}>
              <a
                href={agent.elevateSiteHref}
                target="_blank"
                rel="noopener noreferrer"
              >
                {agent.elevateSiteUrl}
              </a>
            </span>
          </Typography>
        ) : mlsWebsiteUrl ? (
          <Typography type="subHeading" className={classes.agentDetail}>
            <Link size={18} className={classes.agentDetailIcon} />
            <span className={classes.agentDetailText}>
              <a
                href={agent.mlsWebsiteHref}
                target="_blank"
                rel="noopener noreferrer"
              >
                {agent.mlsWebsiteUrl}
              </a>
            </span>
          </Typography>
        ) : null}
        {otherWebsiteUrl ? (
          <Typography type="subHeading" className={classes.agentDetail}>
            <Link size={18} className={classes.agentDetailIcon} />
            <span className={classes.agentDetailText}>
              <a
                href={agent.otherWebsiteHref}
                target="_blank"
                rel="noopener noreferrer"
              >
                {agent.otherWebsiteUrl}
              </a>
            </span>
          </Typography>
        ) : null}
      </div>
      <AgentSocial
        className={classes.socialMedia}
        socialMedia={agent.socialMedia}
      />
      <AgentBio className={classes.agentBio} agentBio={agentBio} />
    </div>
  );
};
export default withStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    maxWidth: "500px",
    padding: theme.spacing.unit * 0.5,
    margin: "auto",
  },
  profileTop: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    marginBottom: theme.spacing.unit * 0.75,
  },
  profileBottom: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    width: "100%",
  },
  photo: {
    width: "120px",
    height: "120px",
    objectFit: "contain",
    position: "relative",
    borderRadius: "50%",
    marginBottom: theme.spacing.unit * 1.25,
  },
  agentDetail: {
    color: theme.colors.gray["800"],
    marginBottom: theme.spacing.unit * 0.5,
    display: "flex",
    alignItems: "center",
  },
  agentDetailIcon: {
    flexShrink: "0",
    display: "flex",
    alignItems: "center",
    marginRight: theme.spacing.unit * 0.5,
    fill: theme.colors.gray["800"],
  },
  agentDetailText: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  socialMedia: {
    margin: "16px 0",
  },
}))(withGlobalContext(AgentProfile));
